import React from 'react';
import PropTypes from 'prop-types';
import { Grid, Accordion, AccordionSummary, AccordionDetails, Box, TextField, Typography, LinearProgress, Chip } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import AccountHeader from './AccountHeader';
import ManufacturerSection from './ManufacturerSection';

const AccountCard = React.memo(({ 
  account, expandedAccordion, setExpandedAccordion, selectedAccounts, 
  toggleAccountSelection, canEdit, editAccount, deleteAccount, 
  calculateProgress, updateAccountStatus, updateGoLiveStatus, getStepsForManufacturer, 
  getStatusesForManufacturer, getStatusColor, getApiUrl, setSaveMessage 
}) => (
  <Grid key={account.id} size={12}>
    <Accordion
      expanded={expandedAccordion === account.id}
      onChange={(event, isExpanded) => {
        setExpandedAccordion(isExpanded ? account.id : null);
      }}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box sx={{ width: '100%' }}>
          <AccountHeader 
            account={account}
            selectedAccounts={selectedAccounts}
            toggleAccountSelection={toggleAccountSelection}
            canEdit={canEdit}
            editAccount={editAccount}
            deleteAccount={deleteAccount}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LinearProgress 
              variant="determinate" 
              value={calculateProgress(account)} 
              sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
            />
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              {Math.round(calculateProgress(account))}% Complete
            </Typography>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            mb: 2,
            fontStyle: 'italic',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5
          }}>
          *Note: Fields marked with 
          <Chip 
            label="Evoke" 
            size="small" 
            color="primary" 
            variant="outlined"
            sx={{ height: 16, fontSize: '0.65rem', fontWeight: 600 }}
          />
          are managed by Evoke stakeholders
        </Typography>
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Account Name"
            value={account.accountName}
            size="small"
            sx={{ maxWidth: 400 }}
            disabled
            slotProps={{
              input: {
                readOnly: true,
                style: { color: 'black' }
              },

              inputLabel: { shrink: true, style: { color: 'black' } }
            }} />
          <TextField
            label={
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Evoke Portal Name
                <Chip 
                  label="Evoke" 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                  sx={{ height: 16, fontSize: '0.65rem', fontWeight: 600 }}
                />
              </Box>
            }
            value={account.evokePortalName || ''}
            size="small"
            sx={{ maxWidth: 400 }}
            disabled
            slotProps={{
              input: {
                readOnly: true,
                style: { color: 'black' }
              },

              inputLabel: { shrink: true, style: { color: 'black' } }
            }} />
          <TextField
            label={
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Go Live Date
                <Chip 
                  label="Evoke" 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                  sx={{ height: 16, fontSize: '0.65rem', fontWeight: 600 }}
                />
              </Box>
            }
            value={account.goLiveDate || ''}
            size="small"
            sx={{ maxWidth: 400 }}
            disabled
            slotProps={{
              input: {
                readOnly: true,
                style: { color: 'black' }
              },

              inputLabel: { shrink: true, style: { color: 'black' } }
            }} />
          <TextField
            label={
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Go-Live Status
                <Chip 
                  label="Evoke" 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                  sx={{ height: 16, fontSize: '0.65rem', fontWeight: 600 }}
                />
              </Box>
            }
            value={account.goLiveStatus ? 'Go-Live' : 'Not Go-Live'}
            size="small"
            sx={{ maxWidth: 400 }}
            disabled
            slotProps={{
              input: {
                readOnly: true,
                style: { color: account.goLiveStatus ? '#1976d2' : 'black', fontWeight: account.goLiveStatus ? 600 : 400 }
              },

              inputLabel: { shrink: true, style: { color: 'black' } }
            }} />
        </Box>
        
        {account.manufacturers.sort((a, b) => a.localeCompare(b)).map(manufacturer => (
          <ManufacturerSection
            key={manufacturer}
            account={account}
            manufacturer={manufacturer}
            calculateProgress={calculateProgress}
            getStepsForManufacturer={getStepsForManufacturer}
          />
        ))}
        

      </AccordionDetails>
    </Accordion>
  </Grid>
));

AccountCard.propTypes = {
  account: PropTypes.object.isRequired,
  expandedAccordion: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setExpandedAccordion: PropTypes.func.isRequired,
  selectedAccounts: PropTypes.array.isRequired,
  toggleAccountSelection: PropTypes.func.isRequired,
  canEdit: PropTypes.bool.isRequired,
  editAccount: PropTypes.func.isRequired,
  deleteAccount: PropTypes.func.isRequired,
  calculateProgress: PropTypes.func.isRequired,
  updateAccountStatus: PropTypes.func.isRequired,
  updateGoLiveStatus: PropTypes.func.isRequired,
  getStepsForManufacturer: PropTypes.func.isRequired,
  getStatusesForManufacturer: PropTypes.func.isRequired,
  getStatusColor: PropTypes.func.isRequired,
  getApiUrl: PropTypes.func.isRequired,
  setSaveMessage: PropTypes.func.isRequired
};

export default AccountCard;